import type React from "react";
import { Box, Group, Stack, Text } from "@mantine/core";
import { Badge, Button } from "@xplatform/design-system";
import {
  IconArrowLeft,
  IconCloudUpload,
  IconFileSpreadsheet,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

interface MappingServiceTopbarProps {
  kind: string;
  pluginId: string;
  filterActive: boolean;
  onBack: () => void;
  onOpenFilter: React.MouseEventHandler<HTMLButtonElement>;
  onRefresh: () => void;
  isLoadingInitial: boolean;
  onOpenUpload: () => void;
  uploadButtonLoading: boolean;
  /** Fetch and save the empty input workbook. Absent for services that do not
   *  take a spreadsheet, so the button simply does not render for them. */
  onDownloadTemplate?: () => void;
  templateDownloading?: boolean;
}

/** Header bar: back button, service title/plugin badge, and the filter /
 * refresh / upload actions. */
const MappingServiceTopbar: React.FC<MappingServiceTopbarProps> = ({
  kind,
  pluginId,
  filterActive,
  onBack,
  onOpenFilter,
  onRefresh,
  isLoadingInitial,
  onOpenUpload,
  uploadButtonLoading,
  onDownloadTemplate,
  templateDownloading = false,
}) => {
  const { t } = useTranslation();
  const titleKey = `mappingServices.kinds.${kind}`;

  return (
    <Box className="mapping-service-page__topbar">
      <Group gap="sm" wrap="nowrap" align="center">
        <Button
          size="sm"
          variant="filled"
          leftSection={<IconArrowLeft size={16} />}
          onClick={onBack}
          aria-label={t("mappingServicePage.actions.backToDashboard")}
        >
          {t("mappingServicePage.actions.back", { defaultValue: "Back" })}
        </Button>
        <Stack gap={2} align="flex-start">
          <Text size="lg" fw={700}>
            {t(titleKey, { defaultValue: kind })}
          </Text>
          <Badge size="xs" color="brand" variant="light">
            {pluginId}
          </Badge>
        </Stack>
      </Group>
      <Group gap="xs">
        <Button
          size="sm"
          variant={filterActive ? "light" : "default"}
          leftSection={<IconFilter size={16} />}
          onClick={onOpenFilter}
        >
          {t("mappingServicePage.jobs.filter")}
        </Button>
        <Button
          size="sm"
          variant="default"
          leftSection={<IconRefresh size={16} />}
          onClick={onRefresh}
          disabled={isLoadingInitial}
          loading={isLoadingInitial}
        >
          {t("mappingServicePage.jobs.refresh")}
        </Button>
        {onDownloadTemplate && (
          // Before Upload, and deliberately: the sheet has to match the
          // accepted headers exactly, so the template is a prerequisite of a
          // successful upload rather than a reference to consult afterwards.
          <Button
            size="sm"
            variant="outline"
            leftSection={<IconFileSpreadsheet size={16} />}
            onClick={onDownloadTemplate}
            loading={templateDownloading}
          >
            {t("mappingServicePage.template.cta", {
              defaultValue: "Download template",
            })}
          </Button>
        )}
        <Button
          size="sm"
          leftSection={<IconCloudUpload size={16} />}
          onClick={onOpenUpload}
          loading={uploadButtonLoading}
        >
          {t("mappingServicePage.upload.cta")}
        </Button>
      </Group>
    </Box>
  );
};

export default MappingServiceTopbar;
